import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cloneDeep, omitBy, set, pick, isEqual } from 'lodash';
import { ROLES } from 'src/constants';
import {
    useGetMapitShipOrdersQuery,
    useGetMapitShipOrdersByAdminQuery,
    useGetMapitShipOrdersCountQuery,
    useGetMapitShipOrdersCountByAdminQuery,
} from 'src/_api/mapitShipApi';

// Properties that should be synced with URL query string
const SYNC_KEYS = [
    'customerName',
    'customerAddress',
    'minOrderGrandTotal',
    'maxOrderGrandTotal',
    'searchKey',
    'pickupLocation',
    'destinationCity',
    'paymentMethod',
    'merchant',
    'fromOrderDate',
    'toOrderDate',
    'shipmentNumber',
    'integrationType',
];

/**
 * Hook to manage URL-synced parameters
 * 
 * Strategy: "Sync on Submit"
 * - filterParams: Local state for form inputs (updates immediately, no URL sync)
 * - params: Applied params synced to URL (only updates on submit/tab change)
 * - This prevents URL pollution and improves performance
 */
export const useOrderParams = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isInitializedRef = useRef(false);
    const lastUrlUpdateRef = useRef(null);

    // Compute initial params from URL (only once)
    const initialParams = useMemo(() => {
        const queryParams = Object.fromEntries([...searchParams]);
        const status = queryParams.status || 'ORDER_PENDING';
        const filteredParams = pick(queryParams, SYNC_KEYS);

        return {
            page: 1,
            limit: 20,
            status,
            ...filteredParams,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    // Applied params (synced to URL) - represents "what user searched for"
    const [params, setParamsState] = useState(initialParams);

    // Filter params (local state) - represents "what user is typing"
    const [filterParams, setFilterParams] = useState(initialParams);

    // Pending URL update ref - to debounce rapid URL updates
    const pendingUrlUpdateRef = useRef(null);

    // Stable URL update function - debounced to prevent UI freeze
    const updateUrl = useCallback((newParams) => {
        const cleanedParams = omitBy(
            newParams,
            (v) => v === '' || v === null || v === undefined
        );

        const urlString = JSON.stringify(cleanedParams);

        // Skip if URL hasn't changed
        if (lastUrlUpdateRef.current === urlString) {
            return;
        }

        // Cancel any pending URL update
        if (pendingUrlUpdateRef.current) {
            cancelAnimationFrame(pendingUrlUpdateRef.current);
        }

        // Defer URL update to next animation frame to avoid blocking UI
        pendingUrlUpdateRef.current = requestAnimationFrame(() => {
            lastUrlUpdateRef.current = urlString;
            setSearchParams(cleanedParams, { replace: true });
            pendingUrlUpdateRef.current = null;
        });
    }, [setSearchParams]);

    // Set applied params AND sync to URL (used for tab change, pagination)
    // Uses setTimeout to batch state updates and prevent blocking
    const setParams = useCallback((updater) => {
        // Use a single update cycle
        setParamsState((prev) => {
            const newParams = typeof updater === 'function' ? updater(prev) : updater;

            // Also update filter params to stay in sync (batched by React)
            setFilterParams(newParams);

            // Defer URL update to avoid blocking the main thread
            setTimeout(() => updateUrl(newParams), 0);

            return newParams;
        });
    }, [updateUrl]);

    // Handler for individual filter changes (NO URL sync - local only)
    const handleFilterChange = useCallback(({ target: { name, value } }) => {
        setFilterParams((prev) => {
            const payload = cloneDeep(prev);
            set(payload, name, value);
            return payload;
        });
    }, []);

    // Ref to track latest filter params for applyFilters
    const filterParamsRef = useRef(filterParams);
    useEffect(() => {
        filterParamsRef.current = filterParams;
    }, [filterParams]);

    // Apply filters: sync filterParams to params and URL, then call optional callback
    const applyFilters = useCallback((onApplied) => {
        // Get current filter params from ref (always fresh)
        const currentFilterParams = filterParamsRef.current;

        // Reset page to 1 when applying filters
        const newParams = { ...currentFilterParams, page: 1 };

        // Update both states synchronously (React 18 batches these)
        setParamsState(newParams);
        setFilterParams(newParams);

        // Defer URL update and callback to next frame
        requestAnimationFrame(() => {
            updateUrl(newParams);
            if (typeof onApplied === 'function') {
                onApplied(newParams);
            }
        });
    }, [updateUrl]);

    // Reset filters to match current applied params
    const resetFilters = useCallback(() => {
        setFilterParams(params);
    }, [params]);

    // Initialize URL on mount
    useEffect(() => {
        if (!isInitializedRef.current) {
            isInitializedRef.current = true;
            updateUrl(params);
        }
    }, [params, updateUrl]);

    return {
        params,           // Applied params (for API calls, URL represents this)
        filterParams,     // Local filter state (for form inputs)
        setParams,        // Set applied params + sync URL (for tab change, pagination)
        handleFilterChange, // Update local filter only (for input changes)
        applyFilters,     // Apply filters on submit
        resetFilters,     // Reset local filters to applied state
    };
};

/**
 * Hook to manage orders fetching with separate query params to prevent unnecessary fetches
 */
export const useOrdersData = (profile, initialParams) => {
    const [queryParams, setQueryParams] = useState(initialParams ?? null);
    const [orders, setOrders] = useState({});
    const [rowCount, setRowCount] = useState(0);

    const isMerchant = profile?.merchant?.role === ROLES.merchant;
    const isAdmin = profile?.role === ROLES.admin;

    // Clean params for API call
    const cleanedQueryParams = useMemo(() =>
        queryParams ? omitBy(queryParams, (v) => !v || ['ALL_ORDERS'].includes(v) || v === 'OPEN') : null,
        [queryParams]
    );

    // Merchant orders query
    const {
        data: merchantOrders,
        isLoading: merchantOrdersLoading,
        isFetching: merchantOrdersFetching,
    } = useGetMapitShipOrdersQuery(cleanedQueryParams, {
        skip: !isMerchant || !cleanedQueryParams,
    });

    // Admin orders query
    const {
        data: adminOrders,
        isLoading: adminOrdersLoading,
        isFetching: adminOrdersFetching,
    } = useGetMapitShipOrdersByAdminQuery(cleanedQueryParams, {
        skip: !isAdmin || !cleanedQueryParams,
    });

    // Update orders when data changes
    useEffect(() => {
        if (isMerchant && merchantOrders) {
            setOrders(merchantOrders);
        } else if (isAdmin && adminOrders) {
            setOrders(adminOrders);
        }
    }, [merchantOrders, adminOrders, isMerchant, isAdmin]);

    // Update row count when orders change
    useEffect(() => {
        if (orders?.totalDocs !== undefined) {
            setRowCount(orders.totalDocs);
        }
    }, [orders?.totalDocs]);

    // Trigger fetch with new params
    const fetchOrders = useCallback((newParams) => {
        setQueryParams(newParams);
    }, []);

    const isLoading = adminOrdersLoading || merchantOrdersLoading;
    const isFetching = adminOrdersFetching || merchantOrdersFetching;

    return {
        orders,
        isLoading,
        isFetching,
        rowCount,
        fetchOrders,
    };
};

/**
 * Hook to manage orders count queries
 */
export const useOrdersCount = (profile) => {
    const isMerchant = profile?.merchant?.role === ROLES.merchant;
    const isAdmin = profile?.role === ROLES.admin;

    const { data: merchantOrdersCount, isLoading: merchantCountLoading } =
        useGetMapitShipOrdersCountQuery({}, { skip: !isMerchant });

    const { data: adminOrdersCount, isLoading: adminCountLoading } =
        useGetMapitShipOrdersCountByAdminQuery({}, { skip: !isAdmin });

    const ordersCount = isAdmin ? adminOrdersCount : merchantOrdersCount;
    const isLoading = adminCountLoading || merchantCountLoading;

    return { ordersCount, isLoading };
};

/**
 * Hook to manage order selection
 */
export const useOrderSelection = (ordersDocs) => {
    const [selectedOrdersIds, setSelectedOrdersIds] = useState([]);

    // Properly memoize selected orders with correct dependency
    const selectedOrders = useMemo(() => {
        if (!ordersDocs || !selectedOrdersIds.length) return [];
        return ordersDocs.filter((order) => selectedOrdersIds.includes(order.id));
    }, [ordersDocs, selectedOrdersIds]);

    const clearSelection = useCallback(() => {
        setSelectedOrdersIds([]);
    }, []);

    const handleSelectionChange = useCallback((ids) => {
        setSelectedOrdersIds(ids);
    }, []);

    return {
        selectedOrdersIds,
        selectedOrders,
        setSelectedOrdersIds: handleSelectionChange,
        clearSelection,
    };
};

/**
 * Hook to manage order dialog state
 */
export const useOrderDialog = (searchParams, onOrdersRefresh) => {
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [openOrderDialog, setOpenOrderDialog] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const ORDER_DIALOG = searchParams.get('ORDER_DIALOG');
    const shipmentId = searchParams.get('shipmentId');

    // Handle shipmentId from URL
    useEffect(() => {
        if (shipmentId) {
            setIsEditing(true);
            setSelectedOrder(shipmentId);
            setOpenOrderDialog(true);
        }
    }, [shipmentId]);

    // Handle ORDER_DIALOG from URL
    useEffect(() => {
        if (ORDER_DIALOG) {
            setOpenOrderDialog(true);
        }
    }, [ORDER_DIALOG]);

    const openDialog = useCallback((row = null) => {
        if (row) {
            setIsEditing(true);
            setSelectedOrder(row.id);
        }
        setOpenOrderDialog(true);
    }, []);

    const closeDialog = useCallback(() => {
        setOpenOrderDialog(false);
        setIsEditing(false);
        setSelectedOrder(null);
        onOrdersRefresh?.();
    }, [onOrdersRefresh]);

    return {
        selectedOrder,
        openOrderDialog,
        isEditing,
        openDialog,
        closeDialog,
    };
};
